import { Flex, Group, Menu, Stack } from '@mantine/core';
import React, { useState } from 'react';
import i18n from '../../../base-dictionary';
import { newCOLORS } from '../../../styles/colors';
import { baseBlack } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { DashboardTemplateModal } from './page-template-modal/dashboard-template-modal';

interface DashboardActionsProps {
  dashboardId: string;
  onAddDashboardWidget: () => void;
  onAddDataEntryWidget: () => void;
  onEditDashboard: () => void;
  dashboardDescription: string;
  isEditingLayout?: boolean;
  onToggleEditLayout?: () => void;
  onSaveLayout?: () => void;
  onCancelLayout?: () => void;
  onResetLayout?: () => void;
  onSaveTemplate?: (success: boolean) => void;
}

export const DashboardActions: React.FC<DashboardActionsProps> = ({
  dashboardId,
  onAddDashboardWidget,
  onAddDataEntryWidget,
  onEditDashboard,
  dashboardDescription,
  isEditingLayout = false,
  onToggleEditLayout,
  onSaveLayout,
  onCancelLayout,
  onResetLayout,
  onSaveTemplate,
}) => {
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const dashboardString = i18n.t('common.dashboard.dashboard');
  const widgetString = i18n.t('common.dashboard.widget');
  const dataEntryString = i18n.t('common.dashboard.data_entry');
  return (
    <>
      <Flex justify="space-between" align="center">
        <Text color={baseBlack} size="regular">
          {dashboardDescription}
        </Text>
        <Group gap="sm">
          {isEditingLayout ? (
            <>
              <Button
                size="sm"
                radius="xl"
                variant="outline"
                color={newCOLORS.darkGray}
                leftSection={<Icon color={newCOLORS.darkGray} name="close" size={16} />}
                onClick={onCancelLayout}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                radius="xl"
                variant="outline"
                color={newCOLORS.darkGray}
                leftSection={<Icon color={newCOLORS.darkGray} name="settings_backup_restore" size={16} />}
                onClick={onResetLayout}
              >
                Reset Layout
              </Button>
              <Button
                size="sm"
                radius="xl"
                variant="primary"
                leftSection={<Icon name="check_circle" size={16} color="white" />}
                onClick={onSaveLayout}
              >
                Save Layout
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                radius="xl"
                variant="outline"
                color={newCOLORS.darkGray}
                leftSection={<Icon color={newCOLORS.darkGray} name="edit" size={16} />}
                onClick={onEditDashboard}
              >
                Edit
              </Button>
              <Button
                size="sm"
                radius="xl"
                variant="outline"
                color={newCOLORS.darkGray}
                leftSection={<Icon color={newCOLORS.darkGray} name="dashboard_customize" size={16} />}
                onClick={onToggleEditLayout}
              >
                Edit Layout
              </Button>
              <Button
                size="sm"
                radius="xl"
                variant="outline"
                color={newCOLORS.darkGray}
                leftSection={<Icon color={newCOLORS.darkGray} name="add_to_photos" size={16} />}
                onClick={() => setIsTemplateModalOpen(true)}
              >
                Save as Template
              </Button>
              <Menu shadow="md" width="auto">
                <Menu.Target>
                  <Button
                    size="sm"
                    radius="xl"
                    variant="primary"
                    leftSection={<Icon name="arrow_drop_down_circle" size={20} color="white" />}
                  >
                    {`Add New ${widgetString}`}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Stack gap="xs" align="center" style={{ padding: '1rem' }}>
                    <Tooltip
                      label={
                        <Stack gap={3}>
                          <Text>{`Add ${dashboardString} ${widgetString}.`}</Text>
                          <Text size="small">{`These widgets display measure data.`}</Text>
                        </Stack>
                      }
                      variant="black"
                      zIndex={2000}
                      position="left"
                      multiline
                      style={{ maxWidth: 350 }}
                    >
                      <Button
                        variant="secondary"
                        radius="xl"
                        onClick={onAddDashboardWidget}
                        leftSection={<Icon name="add_circle" size={16} color="white" />}
                      >
                        {`${dashboardString} ${widgetString}`}
                      </Button>
                    </Tooltip>
                    <Tooltip
                      label={
                        <Stack gap={3}>
                          <Text>{`Add ${dataEntryString} ${widgetString}.`}</Text>
                          <Text size="small">{`These widgets allow you to enter data manually into Bloomfilter. This data can be used to create measures and dashboard widgets.`}</Text>
                        </Stack>
                      }
                      variant="black"
                      zIndex={2000}
                      position="left"
                      style={{ maxWidth: 350 }}
                      multiline
                    >
                      <Button
                        variant="secondary"
                        radius="xl"
                        onClick={onAddDataEntryWidget}
                        leftSection={<Icon name="add_circle" size={16} color="white" />}
                      >
                        {`${dataEntryString} ${widgetString}`}
                      </Button>
                    </Tooltip>
                  </Stack>
                </Menu.Dropdown>
              </Menu>
            </>
          )}
        </Group>
      </Flex>
      <DashboardTemplateModal
        opened={isTemplateModalOpen}
        initialData={{
          page_id: dashboardId,
          name: '',
          description: '',
        }}
        onClose={() => setIsTemplateModalOpen(false)}
        onSuccess={() => onSaveTemplate && onSaveTemplate(true)}
        onError={() => onSaveTemplate && onSaveTemplate(false)}
      />
    </>
  );
};
